import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowsOutCardinal,
  ChatCircleText,
  Copy,
  DownloadSimple,
  ArrowsIn,
  ArrowsInLineHorizontal,
  ArrowsOut,
  Camera,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Columns,
  Cube,
  FloppyDisk,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Moon,
  PencilRuler,
  Scissors,
  Stack,
  Sun,
  Tag,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { SeamPreview } from "@/components/SeamPreview";
import { Countertop3D } from "@/components/Countertop3D";
import { rotatePoints } from "@/components/SlabLayoutBoard";
import { copyOrDownloadPng, svgToPngBlob } from "@/lib/svgSnapshot";
import { api, formatApiErrorDetail, photoHref } from "@/lib/api";
import { InventorySlabPicker } from "@/components/InventorySlabPicker";

const SNAP = 0.25;
const snap = (n) => Math.round(n / SNAP) * SNAP;

// scene presets that back the Shape / Room / All scope buttons on the 3D preview
const SCENE_BY_SCOPE = {
  shape: { cabinets: false, appliances: false, walls: false, uppers: false },
  room: { cabinets: true, appliances: true, walls: true, uppers: false },
  all: { cabinets: true, appliances: true, walls: true, uppers: true },
};

const polyArea = (pts) =>
  Math.abs(
    (pts || []).reduce((s, [x, y], i) => {
      const [x2, y2] = pts[(i + 1) % pts.length];
      return s + x * y2 - x2 * y;
    }, 0) / 2,
  );

const polyContains = (pts, [x, y]) => {
  let hit = false;
  for (let i = 0, j = (pts || []).length - 1; i < (pts || []).length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
};

// the slab photo is cropped to the sheet with zoom + offset, same as the layout board
const photoBox = (s) => {
  const zoom = Math.max(0.1, Number(s.photo_zoom) || 1);
  return {
    x: Number(s.photo_x) || 0,
    y: Number(s.photo_y) || 0,
    width: (Number(s.length_in) || 1) * zoom,
    height: (Number(s.width_in) || 1) * zoom,
  };
};

/** Seam preview + slab boards. Controlled: the parent owns `layout`. */
export const VeinMatchBoard = ({
  quote,
  layout,
  onLayoutChange,
  onSave,
  busy = false,
  onOpenDrawing,
  compact = false,
  onPieceMove,
  slabStock = [],
  edgeStyles = [],
  onHero,
}) => {
  const [selected, setSelected] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  useEffect(() => {
    if (!ctxMenu) return undefined;
    const close = () => setCtxMenu(null);
    window.addEventListener("pointerdown", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [ctxMenu]);
  const [labels, setLabels] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [tight, setTight] = useState(true);
  const [seams, setSeams] = useState(true);
  const [light, setLight] = useState(false);
  const [focus, setFocus] = useState(false);
  const [snapOn, setSnapOn] = useState(true);
  const [viewMode, setViewMode] = useState("2d");
  const [scope, setScope] = useState("room");
  const [editCollapsed, setEditCollapsed] = useState(false);
  const [piecesPanelOpen, setPiecesPanelOpen] = useState(true);
  const piecesPanelInit = useRef(false);
  const effScene =
    viewMode === "cad"
      ? { mode: "light", cabinets: false, appliances: false, walls: false, uppers: false, sinks: false }
      : { mode: "light", ...SCENE_BY_SCOPE[scope] };
  const modeCls = (active) =>
    `inline-flex h-7 items-center rounded-full px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
      active ? "bg-cyan-400 text-black" : "bg-zinc-900 text-zinc-400 ring-1 ring-zinc-700 hover:text-white"
    }`;
  const [snapBusy, setSnapBusy] = useState(false);
  const [textDlg, setTextDlg] = useState(null);
  const openTextCustomer = async () => {
    try {
      const { data } = await api.get(`/quotes/${quote.id}/vein-snapshot/preview`);
      if (!data.to) { toast.error("No phone number on file for this customer."); return; }
      setTextDlg({ to: data.to, message: data.message });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not prepare the text");
    }
  };
  const sendTextCustomer = async () => {
    setSnapBusy(true);
    try {
      const blob = await snapshotPng();
      const fd = new FormData();
      fd.append("file", blob, "vein-match.png");
      fd.append("message", textDlg.message || "");
      const { data } = await api.post(`/quotes/${quote.id}/vein-snapshot/text`, fd);
      toast.success(`Picture texted to ${data.to}`);
      setTextDlg(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "The text could not be sent");
    }
    setSnapBusy(false);
  };
  const snapshotPng = async () => {
    const svg = document.querySelector('[data-testid="seam-preview-svg"]');
    if (!svg) throw new Error("no preview");
    return svgToPngBlob(svg, { background: light ? "#ffffff" : "#09090b" });
  };
  const copyImage = async () => {
    setSnapBusy(true);
    try {
      const how = await copyOrDownloadPng(await snapshotPng(), "vein-match.png");
      toast.success(how === "copied" ? "Seam preview copied — paste it into a text or email" : "Clipboard blocked — PNG downloaded instead");
    } catch {
      toast.error("Could not capture the preview");
    }
    setSnapBusy(false);
  };
  const downloadImage = async () => {
    setSnapBusy(true);
    try {
      const blob = await snapshotPng();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "vein-match.png";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch {
      toast.error("Could not capture the preview");
    }
    setSnapBusy(false);
  };
  const [slideStep, setSlideStep] = useState(1);
  const [lens, setLens] = useState(null);
  const [lensSize, setLensSize] = useState(24);
  const [compare, setCompare] = useState(false);
  const snapshots = layout?.snapshots || [];
  const dragRef = useRef(null);
  // where each piece sat when the quote was last loaded/saved — "Reset positions" goes back here
  const origRef = useRef({});
  useEffect(() => {
    const snap = {};
    (quote?.areas || []).forEach((a) =>
      (a.pieces || []).forEach((p) => {
        snap[p.id] = { area: a.id, x: Number(p.x) || 0, y: Number(p.y) || 0 };
      }),
    );
    origRef.current = snap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id, quote?.updated_at]);

  const slabs = layout?.slabs || [];
  const placements = layout?.placements || {};
  const setLayout = (upd) =>
    onLayoutChange(typeof upd === "function" ? upd(layout || {}) : upd);

  const pieces = useMemo(() => {
    const raw = (quote?.areas || []).flatMap((a) =>
      (a.pieces || [])
        .filter((p) => (p.points || []).length > 2)
        .map((p) => ({
          ...p,
          area_id: a.id,
          area_name: a.name || "Area",
          sqft: Math.round((polyArea(p.points) / 144) * 100) / 100,
        })),
    );
    // older splits left every cut-out on piece A: re-home each to the piece holding its centre
    const inside = (p, X, Y) =>
      X >= 0 && Y >= 0 && X <= (Number(p.length_in) || 0) && Y <= (Number(p.width_in) || 0) &&
      polyContains(p.points, [X, Y]);
    const homed = raw.map((p) => ({ ...p, cutouts: [] }));
    raw.forEach((p) => {
      (p.cutouts || []).forEach((c) => {
        const wx = (Number(p.x) || 0) + (Number(c.x) || 0) + (Number(c.w) || 0) / 2;
        const wy = (Number(p.y) || 0) + (Number(c.y) || 0) + (Number(c.h) || 0) / 2;
        const owner =
          (Number(p.rot) || 0) === 0 &&
          homed.find(
            (q) => q.area_id === p.area_id && (Number(q.rot) || 0) === 0 &&
              inside(q, wx - (Number(q.x) || 0), wy - (Number(q.y) || 0)),
          );
        const tgt = owner || homed.find((q) => q.id === p.id);
        tgt.cutouts.push(
          owner && owner.id !== p.id
            ? { ...c, x: (Number(c.x) || 0) + (Number(p.x) || 0) - (Number(owner.x) || 0), y: (Number(c.y) || 0) + (Number(p.y) || 0) - (Number(owner.y) || 0) }
            : c,
        );
      });
    });
    return homed;
  }, [quote]);

  const patch = (next) => setLayout((l) => ({ ...l, ...next }));

  // free canvas: all areas on one sheet, each piece carries its own offset + spin (layout.free_pos)
  const free = !!layout?.free_canvas;
  const freePos = useMemo(() => layout?.free_pos || {}, [layout?.free_pos]);
  const seedFreePos = () => {
    const seed = {};
    const order = [];
    const byArea = {};
    pieces.forEach((p) => {
      if (!byArea[p.area_id]) { byArea[p.area_id] = []; order.push(p.area_id); }
      byArea[p.area_id].push(p);
    });
    let runX = 0;
    order.forEach((k) => {
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity;
      byArea[k].forEach((p) => {
        const g = rotatePoints(p.points, p.rot || 0);
        x0 = Math.min(x0, Number(p.x) || 0); x1 = Math.max(x1, (Number(p.x) || 0) + g.w); y0 = Math.min(y0, Number(p.y) || 0);
      });
      byArea[k].forEach((p) => { seed[p.id] = { dx: runX - x0, dy: -y0, rot: 0 }; });
      runX += x1 - x0 + 12;
    });
    return seed;
  };
  const toggleFree = () => {
    if (free) patch({ free_canvas: false });
    else patch({ free_canvas: true, free_pos: Object.keys(freePos).length ? freePos : seedFreePos() });
    setSelected(null);
  };
  const resetToSlabLayout = () => {
    patch({ free_canvas: false, free_pos: {}, install_rot: {} });
    setSelected(null);
    toast.success("Snapped back to the slab layout");
  };
  // per-piece installed orientation: turns the piece + its veins in the preview only;
  // the slab cut / vein-match placement (placements[id].rot) stays put. Separate from free-canvas spin.
  const installRot = useMemo(() => layout?.install_rot || {}, [layout?.install_rot]);
  const viewPieces = useMemo(() => {
    if (!free)
      return pieces.map((p) => {
        const pl = placements[p.id];
        const ir = installRot[p.id] || 0;
        if (!pl)
          return ir ? { ...p, rot: ((((Number(p.rot) || 0) + ir) % 360) + 360) % 360 } : p;
        // slab by slab: show each piece exactly where it is nested on its slab
        return {
          ...p,
          area_id: `__slab_${pl.slab ?? 0}`,
          area_name: `Slab ${(pl.slab ?? 0) + 1}`,
          x: Number(pl.x) || 0,
          y: Number(pl.y) || 0,
          rot: ((((Number(pl.rot) || 0) + ir) % 360) + 360) % 360,
        };
      });
    return pieces.map((p) => {
      const f = freePos[p.id] || { dx: 0, dy: 0, rot: 0 };
      return { ...p, area_id: "__free__", area_name: "Free canvas", x: (Number(p.x) || 0) + (f.dx || 0), y: (Number(p.y) || 0) + (f.dy || 0), rot: ((Number(p.rot) || 0) + (f.rot || 0)) % 360 };
    });
  }, [free, pieces, freePos, installRot, placements]);
  const setFree = (pieceId, upd) =>
    patch({ free_pos: { ...freePos, [pieceId]: { ...(freePos[pieceId] || { dx: 0, dy: 0, rot: 0 }), ...upd } } });
  const freeMove = (pieceId, x, y) => {
    const p = pieces.find((q) => q.id === pieceId);
    if (!p) return;
    const f = freePos[pieceId] || { dx: 0, dy: 0 };
    const ddx = x - (Number(p.x) || 0) - (f.dx || 0);
    const ddy = y - (Number(p.y) || 0) - (f.dy || 0);
    const ids = group.includes(pieceId) ? group : [pieceId];
    const next = { ...freePos };
    ids.forEach((id) => {
      const g = next[id] || { dx: 0, dy: 0, rot: 0 };
      next[id] = { ...g, dx: (g.dx || 0) + ddx, dy: (g.dy || 0) + ddy };
    });
    patch({ free_pos: next });
  };
  // group spin: every piece turns about the group's centre so the set stays together
  const spinPiece = (deg) => {
    if (!selected || !free) return;
    remember();
    const ids = group.length > 1 ? group : [selected];
    const centre = (id, rot) => {
      const p = pieces.find((q) => q.id === id);
      const f = freePos[id] || { dx: 0, dy: 0, rot: 0 };
      const g = rotatePoints(p.points, (Number(p.rot) || 0) + rot);
      return { p, f, g, cx: (Number(p.x) || 0) + (f.dx || 0) + g.w / 2, cy: (Number(p.y) || 0) + (f.dy || 0) + g.h / 2 };
    };
    if (deg === null) {
      const next = { ...freePos };
      ids.forEach((id) => { next[id] = { ...(next[id] || { dx: 0, dy: 0 }), rot: 0 }; });
      patch({ free_pos: next });
      return;
    }
    const infos = ids.map((id) => centre(id, freePos[id]?.rot || 0));
    const G = { x: infos.reduce((s, i) => s + i.cx, 0) / infos.length, y: infos.reduce((s, i) => s + i.cy, 0) / infos.length };
    const rad = (deg * Math.PI) / 180;
    const next = { ...freePos };
    infos.forEach((i) => {
      const rot = ((((i.f.rot || 0) + deg) % 360) + 360) % 360;
      const g2 = rotatePoints(i.p.points, (Number(i.p.rot) || 0) + rot);
      const vx = i.cx - G.x, vy = i.cy - G.y;
      const nx = G.x + vx * Math.cos(rad) - vy * Math.sin(rad);
      const ny = G.y + vx * Math.sin(rad) + vy * Math.cos(rad);
      next[i.p.id] = { dx: Math.round((nx - g2.w / 2 - (Number(i.p.x) || 0)) * 100) / 100, dy: Math.round((ny - g2.h / 2 - (Number(i.p.y) || 0)) * 100) / 100, rot };
    });
    patch({ free_pos: next });
  };

  // turn the selected piece's installed orientation (preview only; veins ride along, slab cut unchanged)
  const turnPiece = (deg) => {
    if (!selected) return;
    remember();
    const cur = installRot[selected] || 0;
    const v = deg === null ? 0 : ((((cur + deg) % 360) + 360) % 360);
    patch({ install_rot: { ...installRot, [selected]: v } });
  };
  const setTurnPiece = (val) => {
    if (!selected) return;
    const v = ((((Number(val) || 0) % 360) + 360) % 360);
    patch({ install_rot: { ...installRot, [selected]: Math.round(v * 10) / 10 } });
  };
  // stand every piece in the selected piece's area upright (portrait) — handy for fireplace/mitre sets
  const standUpright = () => {
    const sel = pieces.find((p) => p.id === selected);
    if (!sel) return;
    remember();
    const next = { ...installRot };
    pieces
      .filter((p) => p.area_id === sel.area_id)
      .forEach((p) => {
        const b = rotatePoints(p.points, Number(p.rot) || 0);
        next[p.id] = b.w > b.h ? 90 : 0;
      });
    patch({ install_rot: next });
  };

  // multi-select (free canvas): Shift+click adds/removes pieces from the group
  const [group, setGroup] = useState([]);
  const selectPiece = (id, additive) => {
    if (id === null) { setSelected(null); setGroup([]); return; }
    if (free && additive) {
      setGroup((g) => {
        const base = g.length ? g : selected ? [selected] : [];
        return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
      });
      setSelected(id);
      return;
    }
    setSelected(id);
    setGroup(group.includes(id) && group.length > 1 ? group : []);
  };

  // undo stack: snapshot of placements + installed positions before each edit
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);
  const snapshotNow = () => {
    const positions = {};
    (quote?.areas || []).forEach((a) =>
      (a.pieces || []).forEach((p) => {
        positions[p.id] = { area: a.id, x: Number(p.x) || 0, y: Number(p.y) || 0 };
      }),
    );
    return { placements: layout?.placements || {}, free_pos: layout?.free_pos || {}, install_rot: layout?.install_rot || {}, positions };
  };
  const applyHistory = (snap) => {
    patch({ placements: snap.placements, free_pos: snap.free_pos || {}, install_rot: snap.install_rot || {} });
    if (onPieceMove)
      (quote?.areas || []).forEach((a) =>
        (a.pieces || []).forEach((p) => {
          const o = snap.positions[p.id];
          if (o && (o.x !== (Number(p.x) || 0) || o.y !== (Number(p.y) || 0))) onPieceMove(a.id, p.id, o.x, o.y);
        }),
      );
  };
  const remember = () => {
    historyRef.current = [...historyRef.current.slice(-29), snapshotNow()];
    redoRef.current = [];
    setUndoCount(historyRef.current.length);
    setRedoCount(0);
  };
  const undo = () => {
    const last = historyRef.current.pop();
    if (!last) return;
    redoRef.current = [...redoRef.current.slice(-29), snapshotNow()];
    setUndoCount(historyRef.current.length);
    setRedoCount(redoRef.current.length);
    applyHistory(last);
    toast.success("Undone");
  };
  const redo = () => {
    const next = redoRef.current.pop();
    if (!next) return;
    historyRef.current = [...historyRef.current.slice(-29), snapshotNow()];
    setUndoCount(historyRef.current.length);
    setRedoCount(redoRef.current.length);
    applyHistory(next);
    toast.success("Redone");
  };

  // keep up to two saved arrangements to flip between with the customer
  const saveSnapshot = () => {
    const slot = snapshots.length < 2 ? snapshots.length : 1;
    const rec = { id: Math.random().toString(36).slice(2, 9), name: slot === 0 ? "Option A" : "Option B", placements, saved_at: new Date().toISOString() };
    const next = [...snapshots];
    next[slot] = rec;
    patch({ snapshots: next });
    setCompare(true);
    toast.success(`Saved as ${rec.name}`);
  };
  const applySnapshot = (s) => {
    patch({ placements: s.placements || {} });
    toast.success(`${s.name} applied`);
  };
  const dropSnapshot = (i) => patch({ snapshots: snapshots.filter((_, n) => n !== i) });

  // Add a real slab (or child piece) from inventory as a nesting sheet
  const addSlabFromInventory = (slab, piece) => {
    const invPhoto = (o) => (o?.photos || []).filter((p) => !p?.is_deleted)[0];
    const ph = invPhoto(piece) || invPhoto(slab);
    const rec = {
      id: Math.random().toString(36).slice(2, 9),
      material: slab.slab_color || slab.material || "",
      thickness: slab.thickness || "",
      slab_id: slab.id || "",
      piece_id: piece?.id || "",
      serial_number: piece?.serial_number || slab.serial_number || "",
      slab_code: slab.code || "",
      length_in: Number(piece?.length_in) || Number(slab.length_in) || 126,
      width_in: Number(piece?.width_in) || Number(slab.width_in) || 63,
      photo_url: ph?.storage_path || ph?.thumb_path || "",
    };
    setLayout((l) => ({ ...l, slabs: [...(l.slabs || []), rec] }));
  };

  // Pack each slab's assigned pieces into rows so nothing overlaps (matches the layout)
  const autoNest = () => {
    if (!slabs.length) return;
    const nextPl = { ...placements };
    const bySlab = slabs.map(() => []);
    pieces.forEach((p) => {
      const idx = placements[p.id]?.slab;
      bySlab[idx != null && idx < slabs.length ? idx : 0].push(p);
    });
    bySlab.forEach((list, i) => {
      const sheet = slabs[i];
      const L = (Number(sheet.length_in) || 1) - 1;
      const W = (Number(sheet.width_in) || 1) - 1;
      const sorted = [...list].sort((a, b) => {
        const A = rotatePoints(a.points, 0);
        const B = rotatePoints(b.points, 0);
        return B.w * B.h - A.w * A.h;
      });
      let cx = 1;
      let cy = 1;
      let rowH = 0;
      sorted.forEach((p) => {
        const opts = [0, 90]
          .map((rot) => ({ rot, ...rotatePoints(p.points, rot) }))
          .filter((o) => o.w <= L - 1 && o.h <= W - 1);
        if (!opts.length) return;
        let fit = opts.find((o) => cx + o.w <= L && cy + o.h <= W);
        if (!fit) {
          cx = 1;
          cy += rowH + 1;
          rowH = 0;
          fit = opts.find((o) => cx + o.w <= L && cy + o.h <= W) || opts[0];
        }
        nextPl[p.id] = {
          slab: i,
          x: Math.round(cx * 2) / 2,
          y: Math.round(cy * 2) / 2,
          rot: fit.rot,
        };
        cx += fit.w + 1;
        rowH = Math.max(rowH, fit.h);
      });
    });
    patch({ placements: nextPl });
  };

  const movePiece = (pieceId, x, y) =>
    setLayout((l) => ({
      ...l,
      placements: {
        ...(l.placements || {}),
        [pieceId]: { ...(l.placements || {})[pieceId], x: snap(x), y: snap(y) },
      },
    }));

  // effective placement for a piece — falls back to a sensible default so an
  // unplaced piece can still be moved/rotated (it lands on the first slab)
  const basePl = (pieceId) =>
    placements[pieceId] || { slab: 0, x: 2, y: 2, rot: 0 };

  const nudge = (dx, dy) => {
    if (!selected || !slabs.length) return;
    remember();
    const pl = basePl(selected);
    patch({
      placements: {
        ...placements,
        [selected]: {
          ...pl,
          x: snap(Math.max(0, (pl.x || 0) + dx)),
          y: snap(Math.max(0, (pl.y || 0) + dy)),
        },
      },
    });
  };

  const setAngle = (deg) => {
    if (!selected || !slabs.length) return;
    remember();
    const pl = basePl(selected);
    patch({
      placements: {
        ...placements,
        [selected]: { ...pl, rot: Math.round(Number(deg) * 10) / 10 },
      },
    });
  };

  const rotateBy = (deg) => {
    if (!selected) return;
    const pl = basePl(selected);
    setAngle(((((pl.rot || 0) + deg) % 360) + 360) % 360);
  };

  // copy a neighbour's vein angle so the pattern reads the same across the seam
  const matchNeighbourAngle = (pieceId) => {
    const me = pieces.find((p) => p.id === pieceId);
    const near = pieces.find((p) => p.id !== pieceId && p.area_id === me?.area_id && placements[p.id]);
    if (!near) {
      toast.error("No neighbouring piece to match");
      return;
    }
    const pl = basePl(pieceId);
    remember();
    patch({ placements: { ...placements, [pieceId]: { ...pl, slab: placements[near.id].slab, rot: placements[near.id].rot || 0 } } });
  };

  // mirror the veins on the selected piece (photo sampling only — the cut stays put)
  const flipVeins = (axis) => {
    if (!selected || !slabs.length) return;
    remember();
    const pl = basePl(selected);
    const key = axis === "y" ? "flip_y" : "flip_x";
    patch({ placements: { ...placements, [selected]: { ...pl, [key]: !pl[key] } } });
  };

  const place = (pieceId, slabIndex) =>
    patch({
      placements: {
        ...placements,
        [pieceId]: { slab: slabIndex, x: 2, y: 2, rot: 0 },
      },
    });

  // move an already-placed piece to a different slab, keeping its position/rotation
  const moveToSlab = (pieceId, slabIndex) => {
    remember();
    patch({
      placements: {
        ...placements,
        [pieceId]: { ...basePl(pieceId), slab: slabIndex },
      },
    });
  };

  const startDrag = (e, piece, slabIndex) => {
    e.stopPropagation();
    const svg = e.currentTarget.closest("svg");
    const rect = svg.getBoundingClientRect();
    const s = slabs[slabIndex];
    const pxPerIn = rect.width / (Number(s.length_in) || 1);
    const pl = placements[piece.id];
    dragRef.current = {
      pieceId: piece.id,
      rect,
      pxPerIn,
      slabIndex,
      grabX: (e.clientX - rect.left) / pxPerIn - (pl.x || 0),
      grabY: (e.clientY - rect.top) / pxPerIn - (pl.y || 0),
    };
    setSelected(piece.id);
  };

  // drag the corner handle to rotate a piece about its centre
  const startRotate = (e, piece, slabIndex, gs) => {
    e.stopPropagation();
    const svg = e.currentTarget.closest("svg");
    const rect = svg.getBoundingClientRect();
    const s = slabs[slabIndex];
    const pxPerIn = rect.width / (Number(s.length_in) || 1);
    const pl = placements[piece.id] || {};
    const cxS = rect.left + ((pl.x || 0) + gs.w / 2) * pxPerIn;
    const cyS = rect.top + ((pl.y || 0) + gs.h / 2) * pxPerIn;
    dragRef.current = {
      rotating: true,
      pieceId: piece.id,
      cxS,
      cyS,
      startMouse: (Math.atan2(e.clientY - cyS, e.clientX - cxS) * 180) / Math.PI,
      startRot: Number(pl.rot) || 0,
    };
    setSelected(piece.id);
  };

  const onMove = (e, i) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.rotating) {
      const cur =
        (Math.atan2(e.clientY - d.cyS, e.clientX - d.cxS) * 180) / Math.PI;
      const rot =
        ((Math.round(d.startRot + (cur - d.startMouse)) % 360) + 360) % 360;
      setLayout((l) => ({
        ...l,
        placements: {
          ...(l.placements || {}),
          [d.pieceId]: { ...(l.placements || {})[d.pieceId], rot },
        },
      }));
      return;
    }
    const s = slabs[i];
    if (!s) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pxPerIn = rect.width / (Number(s.length_in) || 1);
    const x = Math.max(0, (e.clientX - rect.left) / pxPerIn - d.grabX);
    const y = Math.max(0, (e.clientY - rect.top) / pxPerIn - d.grabY);
    setLayout((l) => ({
      ...l,
      placements: {
        ...(l.placements || {}),
        [d.pieceId]: {
          ...(l.placements || {})[d.pieceId],
          slab: i,
          x: snapOn ? snap(x) : Math.round(x * 100) / 100,
          y: snapOn ? snap(y) : Math.round(y * 100) / 100,
        },
      },
    }));
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const placedCount = pieces.filter((p) => placements[p.id]).length;
  const unplaced = pieces.filter((p) => !placements[p.id]);
  // first time the pieces load, start the panel collapsed if nothing is off-slab
  useEffect(() => {
    if (piecesPanelInit.current || !pieces.length) return;
    piecesPanelInit.current = true;
    if (unplaced.length === 0) setPiecesPanelOpen(false);
  }, [pieces.length, unplaced.length]);
  const photoCount = slabs.filter((s) => s.photo_url).length;
  const movedPieces = onPieceMove
    ? pieces.filter((p) => {
        const o = origRef.current[p.id];
        return o && (Math.abs(o.x - (Number(p.x) || 0)) > 0.001 || Math.abs(o.y - (Number(p.y) || 0)) > 0.001);
      })
    : [];
  const resetPositions = () =>
    movedPieces.forEach((p) => {
      const o = origRef.current[p.id];
      onPieceMove(p.area_id, p.id, o.x, o.y);
    });
  // slide the selected piece's installed position (not the veins)
  const selPiece = pieces.find((p) => p.id === selected);
  const slidePiece = (dx, dy) => {
    if (!selPiece) return;
    if (free) {
      remember();
      const ids = group.length > 1 ? group : [selPiece.id];
      const next = { ...freePos };
      ids.forEach((id) => {
        const f = next[id] || { dx: 0, dy: 0, rot: 0 };
        next[id] = { ...f, dx: (f.dx || 0) + dx, dy: (f.dy || 0) + dy };
      });
      patch({ free_pos: next });
      return;
    }
    if (!onPieceMove) return;
    remember();
    onPieceMove(selPiece.area_id, selPiece.id, Math.max(0, (Number(selPiece.x) || 0) + dx), Math.max(0, (Number(selPiece.y) || 0) + dy));
  };
  const resetPiece = () => {
    if (!selPiece) return;
    remember();
    if (free) {
      const seed = seedFreePos()[selPiece.id];
      if (seed) setFree(selPiece.id, seed);
    } else {
      const o = origRef.current[selPiece.id];
      if (o && onPieceMove) onPieceMove(selPiece.area_id, selPiece.id, o.x, o.y);
    }
    const nextInstall = { ...installRot };
    delete nextInstall[selPiece.id];
    if (placements[selPiece.id]) patch({ placements: { ...placements, [selPiece.id]: { ...basePl(selPiece.id), x: 2, y: 2, rot: 0, flip_x: false, flip_y: false } }, install_rot: nextInstall });
    else patch({ install_rot: nextInstall });
  };
  const resetCanvas = () => {
    remember();
    patch({ free_pos: seedFreePos() });
  };

  // keyboard: arrows slide the piece (Shift = veins), R / Shift+R rotate, F / Shift+F flip, Ctrl+Z undo, Esc deselect
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
        return;
      }
      if (!selected) return;
      const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (arrows[e.key]) {
        e.preventDefault();
        const [dx, dy] = arrows[e.key];
        if (e.shiftKey) nudge(dx * slideStep, dy * slideStep);
        else slidePiece(dx * slideStep, dy * slideStep);
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        rotateBy(e.shiftKey ? -90 : 90);
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        flipVeins(e.shiftKey ? "y" : "x");
      } else if (free && (e.key === "[" || e.key === "]")) {
        e.preventDefault();
        spinPiece((e.key === "]" ? 1 : -1) * (e.shiftKey ? 90 : 5));
      } else if (e.key === "Escape") selectPiece(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const ctlBtn = (active = false) =>
    `inline-flex h-8 items-center justify-center gap-1 rounded-lg px-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
      active ? "bg-cyan-400 text-black" : "bg-zinc-900 text-zinc-300 ring-1 ring-zinc-800 hover:bg-zinc-800 hover:text-white"
    }`;

  return (
    <div onPointerUp={endDrag} onPointerLeave={endDrag} data-testid="vein-board">
      <div className="flex flex-wrap items-end justify-between gap-4">
        {!compact && (
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Vein match
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {quote.project_name}
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Click a piece to select it, then move &amp; rotate it with the
              controls below — or drag it on its slab in the left column
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1 rounded-full border border-white/[0.07] bg-zinc-900/70 p-1.5 backdrop-blur-xl">
          <button
            type="button"
            data-testid="vein-labels-toggle"
            onClick={() => setLabels(!labels)}
            className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-all duration-200 ${
              labels
                ? "bg-white/[0.11] text-white ring-1 ring-white/15"
                : "text-zinc-500 hover:bg-white/[0.06] hover:text-white"
            }`}
          >
            <Tag size={15} weight="bold" /> Labels
          </button>
          <button
            type="button"
            data-testid="vein-tight-toggle"
            onClick={() => setTight(!tight)}
            title="Pull the preview pieces flush together (preview only — the drawing is not moved)"
            className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-all duration-200 ${
              tight
                ? "bg-white/[0.11] text-white ring-1 ring-white/15"
                : "text-zinc-500 hover:bg-white/[0.06] hover:text-white"
            }`}
          >
            <ArrowsInLineHorizontal size={15} weight="bold" /> Keep together
          </button>
          <button
            type="button"
            data-testid="vein-copy-image"
            onClick={copyImage}
            disabled={snapBusy}
            title="Copy the seam preview as a picture — paste it into a text or email"
            className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-zinc-500 transition-all duration-200 hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
          >
            <Copy size={15} weight="bold" /> {snapBusy ? "Copying…" : "Copy as image"}
          </button>
          <button
            type="button"
            data-testid="vein-download-image"
            onClick={downloadImage}
            disabled={snapBusy}
            title="Download the seam preview as a PNG"
            className="flex h-10 items-center gap-2 rounded-full px-3 text-sm font-semibold text-zinc-500 transition-all duration-200 hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
          >
            <DownloadSimple size={15} weight="bold" />
          </button>
          <button
            type="button"
            data-testid="vein-text-customer"
            onClick={openTextCustomer}
            disabled={snapBusy}
            title="Text this seam preview picture to the customer"
            className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-zinc-500 transition-all duration-200 hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
          >
            <ChatCircleText size={15} weight="bold" /> Text to customer
          </button>
          <button
            type="button"
            data-testid="vein-seams-toggle"
            onClick={() => setSeams(!seams)}
            title="Highlight every seam between touching pieces"
            className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-all duration-200 ${
              seams
                ? "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40"
                : "text-zinc-500 hover:bg-white/[0.06] hover:text-white"
            }`}
          >
            <Scissors size={15} weight="bold" /> Seam lines
          </button>
          <button
            type="button"
            data-testid="vein-light-toggle"
            onClick={() => setLight(!light)}
            title="Light / dark preview background"
            className="flex h-10 items-center rounded-full px-3 text-zinc-400 hover:bg-white/[0.06] hover:text-white"
          >
            {light ? <Moon size={16} weight="bold" /> : <Sun size={16} weight="bold" />}
          </button>
          <button
            type="button"
            data-testid="vein-focus-toggle"
            onClick={() => setFocus(!focus)}
            title={focus ? "Show the slabs again" : "Full-width preview"}
            className={`flex h-10 items-center rounded-full px-3 transition-all duration-200 ${
              focus ? "bg-white/[0.11] text-white ring-1 ring-white/15" : "text-zinc-400 hover:bg-white/[0.06] hover:text-white"
            }`}
          >
            {focus ? <ArrowsIn size={16} weight="bold" /> : <ArrowsOut size={16} weight="bold" />}
          </button>
          <button
            type="button"
            data-testid="vein-zoom-out"
            onClick={() => setZoom((z) => Math.max(0.6, z - 0.2))}
            className="flex h-10 items-center rounded-full px-3 text-zinc-400 hover:bg-white/[0.06] hover:text-white"
          >
            <MagnifyingGlassMinus size={16} weight="bold" />
          </button>
          <button
            type="button"
            data-testid="vein-zoom-in"
            onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
            className="flex h-10 items-center rounded-full px-3 text-zinc-400 hover:bg-white/[0.06] hover:text-white"
          >
            <MagnifyingGlassPlus size={16} weight="bold" />
          </button>
          {zoom !== 1 && (
            <button
              type="button"
              data-testid="vein-zoom-fit"
              onClick={() => setZoom(1)}
              title="Fit to screen"
              className="flex h-10 items-center rounded-full px-3 font-mono-data text-[10px] font-bold text-zinc-400 hover:bg-white/[0.06] hover:text-white"
            >
              FIT
            </button>
          )}
          <button
            type="button"
            data-testid="vein-snapshot"
            onClick={saveSnapshot}
            disabled={!slabs.length}
            title="Save this arrangement as Option A / B to compare"
            className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-zinc-400 transition-all duration-200 hover:bg-white/[0.06] hover:text-white disabled:opacity-40"
          >
            <Camera size={15} weight="bold" /> Snapshot
          </button>
          {snapshots.length > 0 && (
            <button
              type="button"
              data-testid="vein-compare-toggle"
              onClick={() => setCompare(!compare)}
              className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-all duration-200 ${
                compare ? "bg-white/[0.11] text-white ring-1 ring-white/15" : "text-zinc-500 hover:bg-white/[0.06] hover:text-white"
              }`}
            >
              <Columns size={15} weight="bold" /> Compare ({snapshots.length})
            </button>
          )}
          <button
            type="button"
            data-testid="vein-auto-nest"
            onClick={autoNest}
            disabled={!slabs.length}
            className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-zinc-400 transition-all duration-200 hover:bg-white/[0.06] hover:text-white disabled:opacity-40"
          >
            <Stack size={15} weight="bold" /> Auto-nest
          </button>
          {(layout?.cuts || []).length > 0 && (
            <button
              type="button"
              data-testid="vein-remove-all-seams"
              onClick={() => {
                patch({ cuts: [] });
                toast.success("All seams removed");
              }}
              title="Clear every seam"
              className="flex h-10 items-center gap-2 rounded-full border border-red-400/40 bg-red-500/10 px-4 text-sm font-semibold text-red-300 transition-all duration-200 hover:bg-red-500/20"
            >
              <Scissors size={15} weight="bold" /> Remove All Seams
            </button>
          )}
          {onSave && (
            <button
              type="button"
              data-testid="vein-save"
              disabled={busy}
              onClick={onSave}
              className="flex h-10 items-center gap-2 rounded-full bg-white/[0.11] px-5 text-sm font-semibold text-white ring-1 ring-white/15 transition-all duration-200 hover:bg-white/[0.16] disabled:opacity-40"
            >
              <FloppyDisk size={16} weight="bold" />
              {busy ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      <div className={`mt-5 grid gap-4 ${focus ? "" : piecesPanelOpen ? "xl:grid-cols-[minmax(240px,2.6fr)_7fr_minmax(200px,1.8fr)]" : "xl:grid-cols-[minmax(240px,2.6fr)_7fr_2.75rem]"}`}>
        <div className={`min-w-0 ${focus ? "" : "xl:order-2"}`}>
          <div
            data-testid="vein-view-modes"
            className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.07] bg-zinc-900/70 px-3 py-2 backdrop-blur-xl"
          >
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <Cube size={13} weight="bold" className="text-cyan-300" /> 3D Preview Viewer
            </span>
            <div className="flex items-center gap-1">
              {[["2d", "2D"], ["3d", "3D"], ["cad", "CAD"]].map(([m, lbl]) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`vein-mode-${m}`}
                  onClick={() => setViewMode(m)}
                  className={modeCls(viewMode === m)}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {viewMode === "3d" && (
              <>
                <span className="mx-1 h-5 w-px bg-zinc-800" />
                <div className="flex items-center gap-1" data-testid="vein-scope">
                  {[["shape", "Shape"], ["room", "Room"], ["all", "All"]].map(([s, lbl]) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`vein-scope-${s}`}
                      onClick={() => setScope(s)}
                      className={modeCls(scope === s)}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              {free ? "Seam preview · free canvas · all areas combined" : "Seam preview · as nested on the slabs"}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="vein-free-toggle"
                onClick={toggleFree}
                title={free ? "Back to the as-installed arrangement (one block per area)" : "Free canvas — drop the area borders, move and spin any piece anywhere"}
                className={`inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  free ? "bg-amber-400 text-black" : "bg-zinc-900 text-zinc-400 ring-1 ring-zinc-700 hover:text-white"
                }`}
              >
                <ArrowsOutCardinal size={12} weight="bold" /> Free canvas {free ? "on" : "off"}
              </button>
              {(free || Object.keys(installRot).length > 0 || Object.keys(freePos).length > 0) && (
                <button
                  type="button"
                  data-testid="vein-reset-slab-layout"
                  onClick={resetToSlabLayout}
                  title="Snap every piece back to exactly how it is nested on the slabs"
                  className="inline-flex h-7 items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-cyan-200 transition-colors duration-150 hover:bg-cyan-500/20"
                >
                  <ArrowCounterClockwise size={12} weight="bold" /> Reset to slab layout
                </button>
              )}
              {free && (
                <button
                  type="button"
                  data-testid="vein-reset-canvas"
                  onClick={resetCanvas}
                  className="rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
                >
                  Reset canvas
                </button>
              )}
              {free && (
              <button
                type="button"
                data-testid="vein-snap-toggle"
                onClick={() => setSnapOn((s) => !s)}
                title={snapOn ? "Snapping on — edges lock to neighbours. Click to move freely." : "Snapping off — pieces move freely. Click to snap to neighbours."}
                className={`inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  snapOn ? "bg-cyan-400 text-black" : "bg-zinc-900 text-zinc-400 ring-1 ring-zinc-700 hover:text-white"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${snapOn ? "bg-black" : "bg-zinc-500"}`} />
                Snap {snapOn ? "on" : "off"}
              </button>
              )}
              <button
                type="button"
                data-testid="vein-undo"
                onClick={undo}
                disabled={!undoCount}
                title="Undo last move / rotation (Ctrl+Z)"
                className="inline-flex h-7 items-center gap-1.5 rounded-full bg-zinc-900 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 ring-1 ring-zinc-700 transition-colors duration-150 hover:text-white disabled:opacity-40"
              >
                <ArrowCounterClockwise size={12} weight="bold" /> Undo{undoCount ? ` (${undoCount})` : ""}
              </button>
              <button
                type="button"
                data-testid="vein-redo"
                onClick={redo}
                disabled={!redoCount}
                title="Redo (Ctrl+Y)"
                className="inline-flex h-7 items-center gap-1.5 rounded-full bg-zinc-900 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 ring-1 ring-zinc-700 transition-colors duration-150 hover:text-white disabled:opacity-40"
              >
                <ArrowClockwise size={12} weight="bold" /> Redo{redoCount ? ` (${redoCount})` : ""}
              </button>
              {!free && movedPieces.length > 0 && (
                <button
                  type="button"
                  data-testid="vein-reset-positions"
                  onClick={resetPositions}
                  className="rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
                >
                  Reset positions ({movedPieces.length})
                </button>
              )}
              <span className="font-mono-data text-[10px] text-zinc-500">
                {placedCount}/{pieces.length} pieces nested · {photoCount} slab
                photo{photoCount === 1 ? "" : "s"}
              </span>
            </span>
          </div>
          {viewMode === "2d" ? (
          <div
            className={`relative flex h-[min(72vh,820px)] min-h-[420px] flex-col overflow-auto rounded-lg border p-3 ${light ? "border-stone-300 bg-white" : "border-zinc-800 bg-zinc-950"}`}
            data-testid="vein-preview"
            data-light={light ? "1" : "0"}
            onWheel={(e) => {
              if (!e.ctrlKey) return;
              e.preventDefault();
              setZoom((z) => Math.min(4, Math.max(0.6, z - Math.sign(e.deltaY) * 0.2)));
            }}
          >
            {!pieces.length ? (
              <p className="p-8 text-center text-sm text-zinc-500">
                Draw the pieces on the quote first.
              </p>
            ) : (
              <div className="m-auto" style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}>
                <SeamPreview
                  fill
                  free={free}
                  fold={false}
                  snap={snapOn}
                  pieces={viewPieces}
                  slabs={slabs}
                  placements={placements}
                  labels={labels}
                  selected={group.length > 1 ? group : selected}
                  onSelect={selectPiece}
                  light={light}
                  tight={false}
                  seams={seams}
                  seamBadge={seams}
                  onSeamClick={(s) => setLens(s)}
                  onMove={free ? freeMove : undefined}
                  onMoveStart={free || onPieceMove ? () => remember() : undefined}
                  onMarquee={free ? (ids, additive, empty) => {
                    if (empty) { if (!additive) selectPiece(null); return; }
                    const next = additive ? [...new Set([...(group.length ? group : selected ? [selected] : []), ...ids])] : ids;
                    setGroup(next.length > 1 ? next : []);
                    setSelected(next[next.length - 1] || null);
                  } : undefined}
                  onContextMenu={(piece, cx, cy) => setCtxMenu({ piece, x: cx, y: cy })}
                />
              </div>
            )}
            <Dialog open={!!textDlg} onOpenChange={(o) => !o && setTextDlg(null)}>
              <DialogContent className="border-zinc-800 bg-zinc-900 sm:max-w-md" data-testid="vein-text-dialog">
                <DialogHeader>
                  <DialogTitle>Text the seam preview</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-zinc-400">
                  A picture of the preview exactly as shown will be sent to <span className="font-mono-data text-zinc-100" data-testid="vein-text-to">{textDlg?.to}</span>.
                </p>
                <textarea
                  data-testid="vein-text-message"
                  value={textDlg?.message || ""}
                  onChange={(e) => setTextDlg((d) => ({ ...d, message: e.target.value }))}
                  rows={4}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-sm text-zinc-100 outline-none focus:border-cyan-400"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" data-testid="vein-text-cancel" onClick={() => setTextDlg(null)} className="h-9 border-zinc-700 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest">Cancel</Button>
                  <Button data-testid="vein-text-send" disabled={snapBusy} onClick={sendTextCustomer} className="h-9 bg-cyan-400 text-[10px] font-bold uppercase tracking-widest text-black hover:bg-cyan-300">
                    {snapBusy ? "Sending…" : "Send picture"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            {ctxMenu && (
              <div
                data-testid="vein-context-menu"
                onPointerDown={(e) => e.stopPropagation()}
                className="fixed z-50 min-w-[200px] overflow-hidden rounded-lg border border-zinc-700 bg-zinc-950 py-1 text-xs text-zinc-200 shadow-2xl"
                style={{ left: Math.min(ctxMenu.x, window.innerWidth - 220), top: Math.min(ctxMenu.y, window.innerHeight - 260) }}
              >
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-cyan-300">
                  {ctxMenu.piece.label || "Piece"}
                </div>
                {[
                  ["vein-ctx-rot-cw", "Rotate veins 90° →", () => rotateBy(90)],
                  ["vein-ctx-rot-ccw", "Rotate veins 90° ←", () => rotateBy(-90)],
                  ["vein-ctx-rot-180", "Turn veins 180°", () => rotateBy(180)],
                  ["vein-ctx-rot-reset", "Reset rotation (0°)", () => setAngle(0)],
                  ["vein-ctx-match", "Match neighbour's veins", () => matchNeighbourAngle(ctxMenu.piece.id)],
                  ...(slabs.length > 1
                    ? slabs.map((s, i) => [
                        `vein-ctx-slab-${i}`,
                        `Cut from slab ${i + 1}${s.mirror ? " (mirrored)" : ""}`,
                        () => moveToSlab(ctxMenu.piece.id, i),
                      ])
                    : []),
                ].map(([tid, label, fn]) => (
                  <button
                    key={tid}
                    type="button"
                    data-testid={tid}
                    onClick={() => {
                      fn();
                      setCtxMenu(null);
                    }}
                    className="block w-full px-3 py-1.5 text-left transition-colors duration-100 hover:bg-zinc-800"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          ) : (
            <div
              data-testid={viewMode === "cad" ? "vein-cad-wrap" : "vein-3d-wrap"}
              className="relative h-[min(72vh,820px)] min-h-[420px] overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
            >
              {!pieces.length ? (
                <p className="p-8 text-center text-sm text-zinc-500">Draw the pieces on the quote first.</p>
              ) : (
                <Countertop3D
                  quote={quote}
                  slabStock={slabStock}
                  edgeStyles={edgeStyles}
                  scene={effScene}
                  height="100%"
                  toolbar={false}
                  cad={viewMode === "cad"}
                  onHero={onHero}
                />
              )}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-widest text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-0.5 w-4 bg-cyan-400" /> Selected piece
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-0.5 w-4 border-t-2 border-dashed border-amber-400" /> Seam
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 bg-zinc-500 [background-image:repeating-linear-gradient(45deg,transparent_0_2px,#a1a1aa_2px_3px)]" /> No slab photo yet
            </span>
            {onPieceMove && (
              free ? (
                <span data-testid="vein-drag-hint">Drag a piece to slide it · {snapOn ? "edges snap to its neighbours" : "snapping off — free movement"} · right-click to rotate veins · click a seam line for a close-up · Ctrl+wheel to zoom · keys: arrows slide (Shift = veins) · R rotate · F flip · [ ] spin piece · Ctrl+Z undo</span>
              ) : (
                <span data-testid="vein-drag-hint">Shown as nested on the slabs · right-click to rotate veins · click a seam line for a close-up · Ctrl+wheel to zoom · drag a piece on its slab in the left column to reposition</span>
              )
            )}
          </div>

          {lens && (
            <div className="mt-3 flex flex-wrap items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="vein-lens">
              <div className={`w-[360px] max-w-full shrink-0 overflow-hidden rounded-md border ${light ? "border-stone-300 bg-white" : "border-zinc-800 bg-zinc-950"}`}>
                <SeamPreview
                  pieces={pieces}
                  slabs={slabs}
                  placements={placements}
                  labels={false}
                  light={light}
                  tight={tight}
                  seams
                  lens={{ x: lens.x, y: lens.y, size: lensSize }}
                />
              </div>
              <div className="flex min-w-[180px] flex-1 flex-col gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-200">Seam close-up · {lens.len.toFixed(1)}" seam</span>
                <span className="text-xs text-zinc-400">Check that the veins carry across the amber line. Change the window size below or click another seam.</span>
                <div className="flex flex-wrap gap-1.5">
                  {[12, 24, 48].map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`vein-lens-size-${s}`}
                      onClick={() => setLensSize(s)}
                      className={`rounded-md border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest ${
                        lensSize === s ? "border-amber-400/60 bg-amber-500/20 text-amber-100" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                      }`}
                    >
                      {s}" window
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="vein-lens-close"
                  onClick={() => setLens(null)}
                  className="mt-auto w-fit rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
                >
                  Close lens
                </button>
              </div>
            </div>
          )}

          {compare && snapshots.length > 0 && (
            <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="vein-compare">
              {snapshots.map((s, i) => (
                <div key={s.id} className="rounded-lg border border-zinc-800 bg-zinc-900 p-2" data-testid={`vein-compare-${i}`}>
                  <div className="flex items-center gap-2 pb-1.5">
                    <span className="text-xs font-bold text-zinc-200">{s.name}</span>
                    <span className="text-[10px] text-zinc-500">{new Date(s.saved_at).toLocaleString()}</span>
                    <button
                      type="button"
                      data-testid={`vein-compare-use-${i}`}
                      onClick={() => applySnapshot(s)}
                      className="ml-auto rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-cyan-200 hover:bg-cyan-500/20"
                    >
                      Use this
                    </button>
                    <button
                      type="button"
                      data-testid={`vein-compare-drop-${i}`}
                      onClick={() => dropSnapshot(i)}
                      className="rounded-full border border-zinc-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:bg-zinc-800"
                    >
                      Remove
                    </button>
                  </div>
                  <div className={`rounded-md border p-2 ${light ? "border-stone-300 bg-white" : "border-zinc-800 bg-zinc-950"}`}>
                    <SeamPreview pieces={pieces} slabs={slabs} placements={s.placements || {}} labels={false} light={light} tight={tight} seams={seams} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {selected && slabs.length > 0 && (
            <div
              className="mt-3 rounded-xl border border-cyan-500/25 bg-zinc-900/70 p-3 backdrop-blur"
              data-testid="vein-nudge-bar"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-cyan-200">
                  <span className="h-2 w-2 rounded-full bg-cyan-400" />
                  {group.length > 1 ? `${group.length} pieces grouped` : selPiece?.label || "Piece"}
                </span>
                {free && (
                  <span className="text-[10px] text-zinc-500" data-testid="vein-group-hint">
                    {group.length > 1 ? "Move / spin acts on the whole group · Shift+click to add or drop a piece" : "Shift+click other pieces to group them"}
                  </span>
                )}
                <span className="font-mono-data text-[10px] text-zinc-500">
                  {placements[selected] ? `slab ${(placements[selected].slab || 0) + 1} · ${Math.round(placements[selected].rot || 0)}°` : "not nested"}
                </span>
                <div className="ml-auto flex flex-wrap items-center gap-1.5">
                  <button type="button" data-testid="vein-edit-collapse" onClick={() => setEditCollapsed((v) => !v)} className={ctlBtn()} title={editCollapsed ? "Show piece controls" : "Hide piece controls"}>
                    <CaretDown size={13} weight="bold" className={editCollapsed ? "-rotate-90" : ""} /> {editCollapsed ? "Controls" : "Hide"}
                  </button>
                  <button type="button" data-testid="vein-piece-match" onClick={() => matchNeighbourAngle(selected)} className={ctlBtn()} title="Copy the neighbour's slab + vein angle">
                    Match neighbour
                  </button>
                  <button type="button" data-testid="vein-piece-reset" onClick={resetPiece} className={ctlBtn()} title="Reset this piece's position and vein rotation">
                    Reset piece
                  </button>
                  <button type="button" data-testid="vein-piece-deselect" onClick={() => selectPiece(null)} className={ctlBtn()} title="Deselect">
                    ✕
                  </button>
                </div>
              </div>

              {!editCollapsed && (
              <>
              <div className="mt-3 grid gap-3 lg:grid-cols-[auto_auto_1fr]">
                {free && (
                  <div className="rounded-lg bg-zinc-950/70 p-2 ring-1 ring-zinc-800" data-testid="vein-slide-group">
                    <div className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">{free ? "Move piece · on canvas" : "Slide piece · installed spot"}</div>
                    <div className="grid grid-cols-3 gap-1">
                      <span />
                      <button type="button" data-testid="vein-slide-up" onClick={() => slidePiece(0 * slideStep, -1 * slideStep)} className={ctlBtn()} title="Slide up 1&quot;"><CaretUp size={13} weight="bold" /></button>
                      <span />
                      <button type="button" data-testid="vein-slide-left" onClick={() => slidePiece(-1 * slideStep, 0 * slideStep)} className={ctlBtn()} title="Slide left 1&quot;"><CaretLeft size={13} weight="bold" /></button>
                      <button type="button" data-testid="vein-slide-step" onClick={() => setSlideStep((v) => (v === 1 ? 0.25 : 1))} className={ctlBtn(slideStep !== 1)} title="Toggle step size">{slideStep === 1 ? '1"' : '¼"'}</button>
                      <button type="button" data-testid="vein-slide-right" onClick={() => slidePiece(1 * slideStep, 0 * slideStep)} className={ctlBtn()} title="Slide right 1&quot;"><CaretRight size={13} weight="bold" /></button>
                      <span />
                      <button type="button" data-testid="vein-slide-down" onClick={() => slidePiece(0 * slideStep, 1 * slideStep)} className={ctlBtn()} title="Slide down 1&quot;"><CaretDown size={13} weight="bold" /></button>
                      <span />
                    </div>
                  </div>
                )}

                <div className="rounded-lg bg-zinc-950/70 p-2 ring-1 ring-zinc-800" data-testid="vein-vein-group">
                  <div className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">Slide veins · on the slab</div>
                  <div className="grid grid-cols-3 gap-1">
                    <button type="button" data-testid="vein-nudge-up-fine" onClick={() => nudge(0, -0.25)} className={ctlBtn()} title="Veins up 1/4&quot;"><CaretUp size={11} /></button>
                    <button type="button" data-testid="vein-nudge-up" onClick={() => nudge(0, -1)} className={ctlBtn()} title="Veins up 1&quot;"><CaretUp size={13} weight="bold" /></button>
                    <span />
                    <button type="button" data-testid="vein-nudge-left" onClick={() => nudge(-1, 0)} className={ctlBtn()} title="Veins left 1&quot;"><CaretLeft size={13} weight="bold" /></button>
                    <button type="button" data-testid="vein-nudge-left-fine" onClick={() => nudge(-0.25, 0)} className={ctlBtn()} title="Veins left 1/4&quot;"><CaretLeft size={11} /></button>
                    <button type="button" data-testid="vein-nudge-right" onClick={() => nudge(1, 0)} className={ctlBtn()} title="Veins right 1&quot;"><CaretRight size={13} weight="bold" /></button>
                    <button type="button" data-testid="vein-nudge-down-fine" onClick={() => nudge(0, 0.25)} className={ctlBtn()} title="Veins down 1/4&quot;"><CaretDown size={11} /></button>
                    <button type="button" data-testid="vein-nudge-down" onClick={() => nudge(0, 1)} className={ctlBtn()} title="Veins down 1&quot;"><CaretDown size={13} weight="bold" /></button>
                    <button type="button" data-testid="vein-nudge-right-fine" onClick={() => nudge(0.25, 0)} className={ctlBtn()} title="Veins right 1/4&quot;"><CaretRight size={11} /></button>
                  </div>
                </div>

                <div className="rounded-lg bg-zinc-950/70 p-2 ring-1 ring-zinc-800" data-testid="vein-rotate-group">
                  <div className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">Rotate veins</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button type="button" data-testid="vein-rotate-ccw" onClick={() => rotateBy(-90)} className={ctlBtn()} title="Rotate 90° counter-clockwise"><ArrowClockwise size={13} weight="bold" className="-scale-x-100" /> 90°</button>
                    <button type="button" data-testid="vein-rotate" onClick={() => rotateBy(90)} className={ctlBtn()} title="Rotate 90° clockwise"><ArrowClockwise size={13} weight="bold" /> 90°</button>
                    <button type="button" data-testid="vein-rotate-180" onClick={() => rotateBy(180)} className={ctlBtn()}>180°</button>
                    <span className="mx-1 h-5 w-px bg-zinc-800" />
                    {[-10, -1, 1, 10].map((d) => (
                      <button key={d} type="button" data-testid={`vein-rotate-${d < 0 ? "m" : "p"}${Math.abs(d)}`} onClick={() => rotateBy(d)} className={ctlBtn()}>
                        {d > 0 ? "+" : "−"}{Math.abs(d)}°
                      </button>
                    ))}
                    <button type="button" data-testid="vein-rotate-45" onClick={() => rotateBy(45)} className={ctlBtn()}>+45°</button>
                    <button type="button" data-testid="vein-rotate-zero" onClick={() => setAngle(0)} className={ctlBtn((placements[selected]?.rot || 0) === 0)} title="Reset to 0°">0°</button>
                    <span className="mx-1 h-5 w-px bg-zinc-800" />
                    <button type="button" data-testid="vein-flip-x" onClick={() => flipVeins("x")} className={ctlBtn(!!placements[selected]?.flip_x)} title="Mirror veins left ↔ right (F)">Flip ↔</button>
                    <button type="button" data-testid="vein-flip-y" onClick={() => flipVeins("y")} className={ctlBtn(!!placements[selected]?.flip_y)} title="Mirror veins top ↕ bottom (Shift+F)">Flip ↕</button>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="0"
                        max="359"
                        step="1"
                        data-testid="vein-rotate-free"
                        value={placements[selected]?.rot || 0}
                        onChange={(e) => setAngle(e.target.value)}
                        className="h-1.5 w-32 cursor-pointer accent-cyan-400"
                      />
                      <input
                        type="number"
                        min="0"
                        max="359"
                        step="0.5"
                        data-testid="vein-rotate-input"
                        value={placements[selected]?.rot || 0}
                        onChange={(e) => setAngle(e.target.value)}
                        className="h-8 w-16 rounded-lg border border-zinc-800 bg-zinc-950 px-2 font-mono-data text-xs text-zinc-100 outline-none focus:border-cyan-400"
                      />
                      <span className="font-mono-data text-xs text-zinc-500">°</span>
                    </div>
                  </div>
                </div>
              </div>

              {!free && (
                <div className="mt-3 rounded-lg bg-zinc-950/70 p-2 ring-1 ring-zinc-800" data-testid="vein-turn-group">
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Turn piece · installed look</span>
                    <span className="text-[9px] normal-case tracking-normal text-zinc-600">veins turn with the piece · slab cut unchanged</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button type="button" data-testid="vein-turn-ccw" onClick={() => turnPiece(-90)} className={ctlBtn()} title="Turn piece 90° counter-clockwise"><ArrowClockwise size={13} weight="bold" className="-scale-x-100" /> 90°</button>
                    <button type="button" data-testid="vein-turn-cw" onClick={() => turnPiece(90)} className={ctlBtn()} title="Turn piece 90° clockwise"><ArrowClockwise size={13} weight="bold" /> 90°</button>
                    <button type="button" data-testid="vein-turn-180" onClick={() => turnPiece(180)} className={ctlBtn()}>180°</button>
                    <button type="button" data-testid="vein-turn-zero" onClick={() => turnPiece(null)} className={ctlBtn(!installRot[selected])} title="Back to the drawn orientation">0°</button>
                    <span className="mx-1 h-5 w-px bg-zinc-800" />
                    <input type="range" min="0" max="359" step="1" data-testid="vein-turn-range" value={installRot[selected] || 0} onChange={(e) => setTurnPiece(e.target.value)} className="h-1.5 w-32 cursor-pointer accent-cyan-400" />
                    <input type="number" min="0" max="359" step="0.5" data-testid="vein-turn-input" value={installRot[selected] || 0} onChange={(e) => setTurnPiece(e.target.value)} className="h-8 w-16 rounded-lg border border-zinc-800 bg-zinc-950 px-2 font-mono-data text-xs text-zinc-100 outline-none focus:border-cyan-400" />
                    <span className="font-mono-data text-xs text-zinc-500">°</span>
                    <span className="mx-1 h-5 w-px bg-zinc-800" />
                    <button type="button" data-testid="vein-stand-upright" onClick={standUpright} className={ctlBtn()} title="Stand every piece in this area upright — for fireplaces / mitred sets"><ArrowsOut size={13} weight="bold" /> Stand all upright</button>
                  </div>
                </div>
              )}
              {free && (
                <div className="mt-3 rounded-lg bg-zinc-950/70 p-2 ring-1 ring-zinc-800" data-testid="vein-spin-group">
                  <div className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-zinc-500">Spin piece · on canvas ( [ / ] keys, Shift = 90°)</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {[-90, -15, -5, 5, 15, 90].map((d) => (
                      <button key={d} type="button" data-testid={`vein-spin-${d < 0 ? "m" : "p"}${Math.abs(d)}`} onClick={() => spinPiece(d)} className={ctlBtn()}>
                        {d > 0 ? "+" : "−"}{Math.abs(d)}°
                      </button>
                    ))}
                    <button type="button" data-testid="vein-spin-zero" onClick={() => spinPiece(null)} className={ctlBtn(!(freePos[selected]?.rot))} title="Back to the drawn orientation">0°</button>
                    <span className="font-mono-data text-xs text-zinc-500">{Math.round(freePos[selected]?.rot || 0)}°</span>
                  </div>
                </div>
              )}
              {slabs.length > 1 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="vein-move-to-slab-bar">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">Cut from</span>
                  {slabs.map((s, i) => (
                    <button
                      key={s.id || i}
                      type="button"
                      data-testid={`vein-move-to-slab-${i}`}
                      onClick={() => moveToSlab(selected, i)}
                      className={ctlBtn(placements[selected]?.slab === i)}
                    >
                      Slab {i + 1}{s.mirror ? " ↔" : ""}
                    </button>
                  ))}
                </div>
              )}
              </>
              )}
            </div>
          )}
        </div>

        <div className={`space-y-3 ${focus ? "hidden" : "xl:order-1 xl:sticky xl:top-4 xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto xl:pr-1"}`} data-testid="vein-slabs-column">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Slabs · drag a piece to slide it or onto another slab
            </span>
            <button
              type="button"
              data-testid="vein-slab-snap-toggle"
              onClick={() => setSnapOn((v) => !v)}
              title={snapOn ? "Snapping on — pieces drag to a tidy grid. Click to move freely." : "Snapping off — pieces move freely. Click to snap to a grid."}
              className={`inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[9px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                snapOn ? "bg-cyan-400 text-black" : "bg-zinc-900 text-zinc-400 ring-1 ring-zinc-700 hover:text-white"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${snapOn ? "bg-black" : "bg-zinc-500"}`} />
              Snap {snapOn ? "on" : "off"}
            </button>
          </div>
          <InventorySlabPicker
            testId="vein-inv-slab-picker"
            quoteColor={quote?.slab_color}
            thickness={quote?.thickness}
            onPick={addSlabFromInventory}
            className="w-full justify-center"
          />
          {slabs.length === 0 && (
            <div
              className="rounded-lg border border-dashed border-zinc-800 p-6 text-center"
              data-testid="vein-no-slabs"
            >
              <p className="text-sm text-zinc-400">
                No slabs yet. Nest the pieces on the Layout step of the drawing,
                then come back.
              </p>
              {onOpenDrawing ? (
                <button
                  type="button"
                  data-testid="vein-open-drawing"
                  onClick={onOpenDrawing}
                  className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-300 hover:text-white"
                >
                  <PencilRuler size={14} weight="bold" /> Open the slab layout
                </button>
              ) : (
                <Link
                  to={`/quotes/${quote.id}?step=1`}
                  className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-300 hover:text-white"
                >
                  <PencilRuler size={14} weight="bold" /> Open the drawing
                </Link>
              )}
            </div>
          )}
          {slabs.map((s, i) => {
            const on = pieces.filter((p) => placements[p.id]?.slab === i);
            return (
              <div key={s.id || i} data-testid={`vein-slab-${i}`}>
                <div className="flex items-center gap-2 pb-1">
                  <span className="text-xs font-bold text-zinc-200">
                    Slab {i + 1}
                  </span>
                  <span className="truncate text-[11px] text-zinc-500">
                    {s.material || quote.slab_color || "Unassigned"}
                  </span>
                  <span className="font-mono-data ml-auto text-[10px] text-zinc-500">
                    {s.length_in}" x {s.width_in}"
                  </span>
                </div>
                <svg
                  viewBox={`0 0 ${s.length_in} ${s.width_in}`}
                  preserveAspectRatio="xMidYMid meet"
                  style={{ aspectRatio: `${s.length_in} / ${s.width_in}` }}
                  className="w-full rounded border border-zinc-800 bg-stone-200"
                  onPointerMove={(e) => onMove(e, i)}
                  onPointerUp={endDrag}
                  data-testid={`vein-slab-svg-${i}`}
                >
                  {s.photo_url ? (
                    <image
                      href={photoHref(s.photo_url)}
                      preserveAspectRatio="xMidYMid slice"
                      {...photoBox(s)}
                    />
                  ) : (
                    <rect
                      width={s.length_in}
                      height={s.width_in}
                      fill="#e7e5e4"
                    />
                  )}
                  {on.map((p) => {
                    const pl = placements[p.id];
                    const gs = rotatePoints(p.points, pl.rot || 0);
                    const sel = selected === p.id;
                    return (
                      <g
                        key={p.id}
                        data-testid={`vein-slab-piece-${p.id}`}
                        transform={`translate(${pl.x || 0} ${pl.y || 0})`}
                        onPointerDown={(e) => startDrag(e, p, i)}
                        className="cursor-grab"
                      >
                        <polygon
                          points={gs.pts.map(([x, y]) => `${x},${y}`).join(" ")}
                          fill={
                            sel ? "rgba(34,211,238,0.28)" : "rgba(34,197,94,0.14)"
                          }
                          stroke={sel ? "#0e7490" : "#15803d"}
                          strokeWidth={Math.max(0.2, s.length_in / 650)}
                        />
                        <text
                          x="2"
                          y={Math.max(2.2, s.length_in / 55)}
                          fill="#0f172a"
                          fontSize={Math.max(1.6, s.length_in / 90)}
                          fontWeight="bold"
                          className="pointer-events-none select-none"
                        >
                          {p.label}
                        </text>
                        {sel &&
                          (() => {
                            const R = Math.max(2.6, s.length_in / 42);
                            const g = R * 0.5;
                            return (
                              <g
                                data-testid={`vein-rotate-handle-${p.id}`}
                                transform={`translate(${gs.w} 0)`}
                                className="cursor-grab"
                                onPointerDown={(e) =>
                                  startRotate(e, p, i, gs)
                                }
                              >
                                <circle
                                  r={R}
                                  fill="#0891b2"
                                  stroke="#ffffff"
                                  strokeWidth={R * 0.14}
                                />
                                <path
                                  d={`M 0 ${-g} A ${g} ${g} 0 1 1 ${-g} 0`}
                                  fill="none"
                                  stroke="#ffffff"
                                  strokeWidth={R * 0.18}
                                  strokeLinecap="round"
                                />
                                <polygon
                                  points={`0,${-g - R * 0.3} 0,${-g + R * 0.3} ${R * 0.4},${-g}`}
                                  fill="#ffffff"
                                />
                              </g>
                            );
                          })()}
                      </g>
                    );
                  })}
                </svg>
              </div>
            );
          })}
        </div>

        <div
          className={`${focus ? "hidden" : "xl:order-3"}`}
          data-testid="vein-pieces-panel"
        >
          {piecesPanelOpen ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3 xl:sticky xl:top-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Pieces{unplaced.length ? ` · ${unplaced.length} not on a slab` : ""}
                </span>
                <button
                  type="button"
                  data-testid="vein-pieces-collapse"
                  onClick={() => setPiecesPanelOpen(false)}
                  title="Collapse the pieces panel"
                  className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                >
                  <CaretRight size={14} weight="bold" />
                </button>
              </div>
              {unplaced.length > 0 ? (
                <div className="space-y-2">
                  {unplaced.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-2"
                    >
                      <span className="truncate text-xs text-zinc-300">
                        {p.label} · {p.sqft} ft²
                      </span>
                      <button
                        type="button"
                        data-testid={`vein-place-${p.id}`}
                        onClick={() => place(p.id, 0)}
                        className="shrink-0 rounded-full border border-indigo-400/40 bg-indigo-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20"
                      >
                        Place
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-500">Every piece is on a slab.</p>
              )}
            </div>
          ) : (
            <button
              type="button"
              data-testid="vein-pieces-expand"
              onClick={() => setPiecesPanelOpen(true)}
              title="Show the pieces panel"
              className="flex h-full min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-zinc-400 transition-colors hover:text-white xl:min-h-[200px] xl:flex-col"
            >
              <CaretLeft size={14} weight="bold" />
              <span className="text-[9px] font-bold uppercase tracking-widest xl:[writing-mode:vertical-rl] xl:rotate-180">
                Pieces{unplaced.length ? ` (${unplaced.length})` : ""}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function VeinMatch() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [slabStock, setSlabStock] = useState([]);
  const [edgeStyles, setEdgeStyles] = useState([]);
  const [layout, setLayout] = useState({ slabs: [], placements: {} });
  const [busy, setBusy] = useState(false);
  const [piecesDirty, setPiecesDirty] = useState(false);
  const savedLayoutRef = useRef("");
  const [leaveTo, setLeaveTo] = useState(null);
  const navigate = useNavigate();
  const dirty = piecesDirty || (savedLayoutRef.current && JSON.stringify(layout) !== savedLayoutRef.current);

  // unsaved moves: warn on tab close / reload
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  const guardNav = (to) => (e) => {
    if (!dirty) return;
    e.preventDefault();
    setLeaveTo(to);
  };

  // catch every in-app link (sidebar, header, breadcrumbs) while dirty — capture phase runs before React Router
  useEffect(() => {
    if (!dirty) return;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target?.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download") || a.closest("[data-testid='vein-board']")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const base = "/admin";
      let path = url.pathname.startsWith(base) ? url.pathname.slice(base.length) || "/" : url.pathname;
      path += url.search;
      const here = window.location.pathname.slice(base.length) + window.location.search;
      if (path === here) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaveTo(path);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [dirty]);

  // local draft: unsaved moves survive a crash / refresh
  const draftKey = `vein-draft-${id}`;
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (!quote) return;
    if (!dirty) {
      if (!draft) localStorage.removeItem(draftKey);
      return;
    }
    const t = setTimeout(() => {
      const positions = {};
      (quote.areas || []).forEach((a) => (a.pieces || []).forEach((p) => { positions[p.id] = { area: a.id, x: Number(p.x) || 0, y: Number(p.y) || 0 }; }));
      localStorage.setItem(draftKey, JSON.stringify({ layout, positions, piecesDirty, at: new Date().toISOString() }));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, layout, quote, piecesDirty, draft]);
  const restoreDraft = () => {
    if (!draft) return;
    setLayout(draft.layout);
    Object.entries(draft.positions || {}).forEach(([pid, o]) => movePiece(o.area, pid, o.x, o.y));
    if (!draft.piecesDirty) setPiecesDirty(false);
    setDraft(null);
    toast.success("Draft restored — remember to save");
  };
  const discardDraft = () => {
    localStorage.removeItem(draftKey);
    setDraft(null);
  };

  // sliding a piece in the seam preview moves it on the quote drawing as well
  const movePiece = (areaId, pieceId, x, y) => {
    setQuote((q) => ({
      ...q,
      areas: (q.areas || []).map((a) =>
        a.id !== areaId
          ? a
          : { ...a, pieces: (a.pieces || []).map((p) => (p.id === pieceId ? { ...p, x, y } : p)) },
      ),
    }));
    setPiecesDirty(true);
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/quotes/${id}`);
    setQuote(data);
    api.get("/slabs").then((r) => setSlabStock(r.data || [])).catch(() => {});
    api
      .get("/settings/pricing")
      .then((r) => setEdgeStyles(r.data?.edge_styles || []))
      .catch(() => {});
    const lay = data.layout && data.layout.slabs ? data.layout : { slabs: [], placements: {} };
    setLayout(lay);
    savedLayoutRef.current = JSON.stringify(lay);
    try {
      const raw = localStorage.getItem(`vein-draft-${id}`);
      const d = raw ? JSON.parse(raw) : null;
      if (d && JSON.stringify(d.layout) !== JSON.stringify(lay)) setDraft(d);
      else if (d) {
        const changed = Object.entries(d.positions || {}).some(([pid, o]) => {
          const p = (data.areas || []).flatMap((a) => a.pieces || []).find((x) => x.id === pid);
          return p && (o.x !== (Number(p.x) || 0) || o.y !== (Number(p.y) || 0));
        });
        if (changed) setDraft(d); else localStorage.removeItem(`vein-draft-${id}`);
      }
    } catch { /* ignore a corrupt draft */ }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const setPortalFlag = async (show) => {
    setQuote((q) => ({ ...q, show_vein_in_portal: show }));
    try {
      await api.patch(`/quotes/${id}/portal-flags`, {
        show_vein_in_portal: show,
      });
      toast.success(
        show ? "Shown in the customer portal" : "Hidden from the portal",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not change that",
      );
    }
  };

  const save = async () => {
    setBusy(true);
    let ok = false;
    try {
      // moved pieces need the whole quote saved (drawing + layout); otherwise layout only
      const { data } = piecesDirty
        ? await api.put(`/quotes/${id}`, { ...quote, layout })
        : await api.put(`/quotes/${id}/layout`, layout);
      setQuote(data);
      setPiecesDirty(false);
      savedLayoutRef.current = JSON.stringify(layout);
      toast.success("Vein match saved");
      ok = true;
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save the layout",
      );
    }
    setBusy(false);
    return ok;
  };

  if (!quote)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Vein match" />
      </div>
    );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={`${quote.code} / vein match`} />
      <main className="relative z-10 px-4 pb-24 pt-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/quotes/${id}/layout`}
            onClick={guardNav(`/quotes/${id}/layout`)}
            data-testid="vein-back-to-layout-screen"
            className="flex h-[52px] items-center gap-2 rounded-full border border-white/[0.07] bg-zinc-900/70 px-5 text-sm font-semibold text-zinc-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl transition-all duration-200 hover:bg-white/[0.08] hover:text-white"
          >
            <ArrowLeft size={16} weight="bold" /> Back to slab layout
          </Link>
          <Link
            to={`/quotes/${id}?step=3`}
            onClick={guardNav(`/quotes/${id}?step=3`)}
            data-testid="vein-go-to-quote"
            className="ml-auto flex h-[52px] items-center gap-2 rounded-full border border-indigo-400/25 bg-indigo-500/10 px-5 text-sm font-semibold text-indigo-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl transition-all duration-200 hover:bg-indigo-500/20 hover:text-white"
          >
            Go to quote
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>

        <div className="mt-3">
          {draft && (
            <div data-testid="vein-draft-banner" className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3">
              <span className="text-sm text-amber-100">
                Unsaved vein moves from {new Date(draft.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} were recovered.
              </span>
              <div className="ml-auto flex gap-2">
                <Button data-testid="vein-draft-restore" onClick={restoreDraft} className="h-8 bg-amber-500 text-[10px] font-bold uppercase tracking-widest text-black hover:bg-amber-400">Restore draft</Button>
                <Button variant="outline" data-testid="vein-draft-discard" onClick={discardDraft} className="h-8 border-zinc-700 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest">Discard</Button>
              </div>
            </div>
          )}
          {dirty && (
            <div data-testid="vein-unsaved-badge" className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" /> Unsaved changes
            </div>
          )}
          <VeinMatchBoard
            quote={quote}
            layout={layout}
            onLayoutChange={setLayout}
            onSave={save}
            busy={busy}
            onPieceMove={movePiece}
            slabStock={slabStock}
            edgeStyles={edgeStyles}
            onHero={(hero) =>
              setQuote((q) => ({ ...q, design: { ...(q.design || {}), hero } }))
            }
          />
        </div>

        <label
          data-testid="vein-portal-toggle"
          className="mt-4 flex w-fit cursor-pointer items-center gap-2.5 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-300"
        >
          <input
            type="checkbox"
            data-testid="vein-portal-checkbox"
            checked={quote.show_vein_in_portal !== false}
            onChange={(e) => setPortalFlag(e.target.checked)}
            className="h-4 w-4 accent-cyan-400"
          />
          Show this vein match in the customer portal
        </label>
        <Dialog open={!!leaveTo} onOpenChange={(o) => !o && setLeaveTo(null)}>
          <DialogContent className="border-zinc-800 bg-zinc-900 sm:max-w-md" data-testid="vein-leave-dialog">
            <DialogHeader>
              <DialogTitle>Unsaved vein match</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-300">You moved or rotated pieces but haven&apos;t saved. Save before leaving?</p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" data-testid="vein-leave-stay" onClick={() => setLeaveTo(null)} className="h-9 border-zinc-700 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest">
                Stay
              </Button>
              <Button variant="outline" data-testid="vein-leave-discard" onClick={() => { const to = leaveTo; setLeaveTo(null); setPiecesDirty(false); savedLayoutRef.current = JSON.stringify(layout); localStorage.removeItem(draftKey); navigate(to); }} className="h-9 border-red-500/30 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-red-300">
                Leave without saving
              </Button>
              <Button data-testid="vein-leave-save" disabled={busy} onClick={async () => { const to = leaveTo; if (await save()) { setLeaveTo(null); navigate(to); } }} className="h-9 bg-amber-500 text-[10px] font-bold uppercase tracking-widest text-black hover:bg-amber-400">
                {busy ? "Saving…" : "Save & leave"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
